// Orca on a locked feature: it explains what the feature gives and which plan
// opens it — it never answers with the feature's own content (that would be
// a way around the plan). The real API applies the same rule to answers.
import type { AskCtx } from '@/features/ai/contexts';
import i18n from '@/i18n';
import { usd } from '@/lib/format';
import { PLAN_LABEL, type PlanKey } from '@/hooks/useCaps';
import type { Feature } from '@/lib/features';
import type { Answer } from '@/types';
import { exampleQuestion, FEATURE_INFO, featureQuestion } from './catalog';

type Offer = { teaser?: string; price?: string; trial?: boolean };
const t = (k: string, o?: object) => (i18n.t as unknown as (k: string, o?: object) => string)(`plans:ans.${k}`, o);

/**
 * A locked feature, told so the user sees what they would pay for: what
 * Orca already found for them (counts from the server, never the content),
 * what they would get, an example message, how it works with the rest of
 * Orca, and the plan / trial. `mode: 'example'` leads with the example.
 */
export function lockedAnswer(f: Feature, need: PlanKey, planName: string, o: Offer = {}, mode: 'value' | 'example' = 'value'): Answer {
  const info = FEATURE_INFO[f];
  const title = info?.title ?? i18n.t('plans:fallback');
  const how = need === 'plus'
    ? t(o.trial ? 'howTrial' : 'howPlus', { price: o.price ?? usd(16.9, 2) })
    : t('howPremium', { price: o.price ?? usd(24.9, 2) });
  return {
    lead: mode === 'example' ? t('exLead', { title }) : o.teaser ?? `${title} — ${i18n.t(need === 'premium' ? 'plans:lockLow.premium' : 'plans:lockLow.plus')}.`,
    known: `${info?.lead ?? ''} ${info ? `${info.points.join('. ')}.` : ''}`.trim(),
    scenario: `${info?.example ?? ''} ${info?.synergy ?? ''}`.trim(),
    risk: `${how} ${t('onlyExplain', { plan: planName })}`,
    labels: [t('l1'), t('l2'), t('l3')],
    action: { kind: 'upgrade', feature: f, label: need === 'plus' && o.trial ? i18n.t('plans:cta.tryPlus') : i18n.t('plans:cta.more', { plan: PLAN_LABEL[need] }) },
  };
}

/** What the Orca bar talks about on a locked feature's preview. */
export const lockedCtx = (f: Feature, need: PlanKey, planName: string, trial: boolean, o: Offer = {}): AskCtx => {
  const info = FEATURE_INFO[f];
  return {
    kind: 'page', icon: info?.icon ?? 'lock', label: `${info?.title ?? i18n.t('plans:fallback')} · ${PLAN_LABEL[need]}`, tag: info?.title ?? i18n.t('plans:page.plans'),
    question: featureQuestion(f), lockedFeature: f,
    suggestions: [featureQuestion(f), exampleQuestion(f), i18n.t('ai:product.diff', { a: PLAN_LABEL[need], b: planName }), ...(trial && need === 'plus' ? [i18n.t('ai:product.trial')] : [])],
    answer: lockedAnswer(f, need, planName, { ...o, trial }),
  };
};

/**
 * Questions about Orca's plans and features («Что даёт Premium?», «Чем Plus
 * отличается от Free?», «Как работает пробный Plus?»). They are answered from
 * the plan catalog, cost no question from the daily quota and work even when
 * the quota is used up — every other question still counts as usual.
 */
// only questions that name a plan, the subscription or the quota (a market question like
// «что даёт снижение ставки» is a normal question and counts)
const PRODUCT_RE = /(тариф|подписк|\bplus\b|\bpremium\b|\bfree\b|пробн(ый|ого|ом) (plus|период)|trial|лимит вопрос|больше вопрос|subscription|\bplans?\b|pricing|abo(nnement)?\b|tarif|probezeit|testphase|fragen-limit)/i;
export const isProductQuestion = (q: string) => PRODUCT_RE.test(q);

type Catalog = { plans: Array<{ id: PlanKey; name: string; price: string; tagline: string; features: string[] }>; compare: Array<{ label: string; values: [string, string, string] }>; trial: string };
const ORDER: PlanKey[] = ['free', 'plus', 'premium'];

export function productAnswer(q: string, cat: Catalog, mine: PlanKey, trialAvailable: boolean, missed: string[] = []): Answer {
  const named = ORDER.filter((p) => new RegExp(p === 'free' ? '\\bfree\\b|фри' : p === 'plus' ? '\\bplus\\b|плюс' : '\\bpremium\\b|премиум', 'i').test(q));
  const next = ORDER[Math.min(2, ORDER.indexOf(mine) + 1)];
  const target = named.find((p) => p !== mine) ?? named[0] ?? next;
  const plan = cat.plans.find((p) => p.id === target)!;
  const mineName = cat.plans.find((p) => p.id === mine)!.name;
  const action = { kind: 'upgrade' as const, feature: 'compare', label: i18n.t('plans:up.compare') };
  if (/пробн|trial|probe|testphase|testzeit|gratis testen|kostenlos testen/i.test(q)) {
    return {
      lead: t(trialAvailable ? 'trialYes' : 'trialNo'),
      known: t('trialKnown', { trial: cat.trial, feats: cat.plans.find((p) => p.id === 'plus')!.features.slice(1, 5).join('; ') }),
      scenario: t('trialScen'),
      risk: t('free'),
      labels: [t('tl1'), t('tl2'), t('tl3')],
      action,
    };
  }
  // what changes from the user's plan to the one asked about
  const a = ORDER.indexOf(mine), b = ORDER.indexOf(target);
  const diff = cat.compare.filter((r) => r.values[a] !== r.values[b]).map((r) => `${r.label}: ${r.values[a] === '—' ? t('none') : r.values[a]} → ${r.values[b] === '—' ? t('none') : r.values[b]}`);
  return {
    lead: target === mine ? t('mine', { plan: plan.name, tagline: plan.tagline }) : t('lead', { plan: plan.name, price: plan.id === 'free' ? i18n.t('plans:cmp.free') : i18n.t('plans:up.perMonth', { price: plan.price }), tagline: plan.tagline }),
    known: t('known', { plan: plan.name, feats: plan.features.join('; ') }),
    scenario: target === mine || !diff.length ? t('full')
      : `${t('vs', { plan: mineName, diff: diff.slice(0, 6).join('; ') })}${missed.length ? ` ${t('missed', { plan: mineName, list: missed.slice(0, 3).join(' ') })}` : ''}`,
    risk: target === 'premium' ? `${t('premiumNoTrial')} ${t('free')}` : `${trialAvailable && target === 'plus' ? `${t('trialHint')} ` : ''}${t('free')}`,
    labels: [t('pl1'), t('pl2'), t('pl3')],
    action,
  };
}
